import React from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Chip,
  Paper,
  Typography,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SkillFilterSelector from './SkillFilterSelector';
import { Skill } from '../../types';

interface SearchFiltersProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  availableTags: string[];
  contentType: string;
  requiredSkills?: Skill[];
  desiredSkills?: Skill[];
  forbiddenSkills?: Skill[];
  onRequiredSkillsChange?: (skills: Skill[]) => void;
  onDesiredSkillsChange?: (skills: Skill[]) => void;
  onForbiddenSkillsChange?: (skills: Skill[]) => void;
}

const SearchFilters: React.FC<SearchFiltersProps> = ({
  searchQuery,
  onSearchQueryChange,
  selectedTags,
  onTagsChange,
  availableTags,
  contentType,
  requiredSkills,
  desiredSkills,
  forbiddenSkills,
  onRequiredSkillsChange,
  onDesiredSkillsChange,
  onForbiddenSkillsChange,
}) => {
  const hasSkillFilters = requiredSkills !== undefined &&
                          desiredSkills !== undefined &&
                          forbiddenSkills !== undefined &&
                          onRequiredSkillsChange !== undefined &&
                          onDesiredSkillsChange !== undefined &&
                          onForbiddenSkillsChange !== undefined;
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SearchIcon />
        Filtres
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        {/* Search by keywords */}
        <TextField
          fullWidth
          label="Mots-clés"
          placeholder="Rechercher..."
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          size="small"
          variant="outlined"
        />

        {/* Filter by tags */}
        <Autocomplete
          multiple
          options={availableTags}
          value={selectedTags}
          onChange={(_, newValue) => onTagsChange(newValue)}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tags"
              placeholder="Sélectionner..."
              size="small"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                {...getTagProps({ index })}
                color="primary"
                size="small"
              />
            ))
          }
        />

        {/* Skill Filters - only for patterns */}
        {hasSkillFilters && (
          <>
            <Divider sx={{ my: 1 }} />
            <SkillFilterSelector
              requiredSkills={requiredSkills!}
              desiredSkills={desiredSkills!}
              forbiddenSkills={forbiddenSkills!}
              onRequiredSkillsChange={onRequiredSkillsChange!}
              onDesiredSkillsChange={onDesiredSkillsChange!}
              onForbiddenSkillsChange={onForbiddenSkillsChange!}
            />
          </>
        )}
      </Box>

      {/* Active filters summary */}
      {(searchQuery ||
        selectedTags.length > 0 ||
        (requiredSkills && requiredSkills.length > 0) ||
        (desiredSkills && desiredSkills.length > 0) ||
        (forbiddenSkills && forbiddenSkills.length > 0)) && (
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Filtres actifs:
          </Typography>
          {searchQuery && (
            <Chip
              label={`"${searchQuery}"`}
              size="small"
              sx={{ mb: 0.5, mr: 0.5 }}
              onDelete={() => onSearchQueryChange('')}
            />
          )}
          {selectedTags.length > 0 && (
            <Chip
              label={`${selectedTags.length} tag(s)`}
              size="small"
              color="primary"
              sx={{ mb: 0.5, mr: 0.5 }}
              onDelete={() => onTagsChange([])}
            />
          )}
          {requiredSkills && requiredSkills.length > 0 && (
            <Chip
              label={`${requiredSkills.length} obligatoire(s)`}
              size="small"
              color="success"
              sx={{ mb: 0.5, mr: 0.5 }}
              onDelete={() => onRequiredSkillsChange?.([])}
            />
          )}
          {desiredSkills && desiredSkills.length > 0 && (
            <Chip
              label={`${desiredSkills.length} souhaité(s)`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mb: 0.5, mr: 0.5 }}
              onDelete={() => onDesiredSkillsChange?.([])}
            />
          )}
          {forbiddenSkills && forbiddenSkills.length > 0 && (
            <Chip
              label={`${forbiddenSkills.length} interdit(s)`}
              size="small"
              color="error"
              sx={{ mb: 0.5, mr: 0.5 }}
              onDelete={() => onForbiddenSkillsChange?.([])}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

export default SearchFilters;
